import clsx from "clsx";
import React from "react";
import {
  ListItemButton,
  ListItemButtonProps,
  ListItemAvatar,
  ListItemText,
  Box,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import { CardAvatar } from "../../card/CardAvatar";
import { ClientInfoDto } from "@growth-machine-llc/stridist-api-client";
import { RadioIndicator } from "./RadioIndicator";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2.5, 1),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },

  content: {
    display: "flex",
    alignItems: "center",
    flex: 1,
    minWidth: 0,
  },

  itemAvatar: {
    minWidth: theme.spacing(6),
    [theme.breakpoints.up("md")]: {
      minWidth: theme.spacing(7),
    },
  },

  avatar: {
    border: "2px solid",
    borderColor: theme.palette.grey[50],
    width: theme.spacing(4.5),
    height: theme.spacing(4.5),
    fontSize: 12,
    fontWeight: "bold",
    backgroundColor: theme.palette.avatar,
    [theme.breakpoints.up("md")]: {
      width: theme.spacing(7),
      height: theme.spacing(7),
      margin: theme.spacing(0, 2, 0, 0),
      fontSize: 18,
    },
  },

  primaryText: {
    color: theme.palette.secondary.main,
    fontWeight: 600,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    [theme.breakpoints.up("md")]: {
      fontSize: 18,
    },
  },

  secondaryText: {
    color: theme.palette.text.secondary,
    fontWeight: 500,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    [theme.breakpoints.up("md")]: {
      fontSize: 16,
    },
  },

  selector: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginLeft: theme.spacing(1),
    pointerEvents: "auto",
  },
}));

export interface ClientListItemProps extends ListItemButtonProps {
  client: ClientInfoDto;
  selected?: boolean;
  singleSelect?: boolean;
}

export function ClientListItem({
  className,
  client,
  selected,
  singleSelect,
  ...other
}: ClientListItemProps) {
  const s = useStyles();

  const name = client.displayName || client.username || "";
  const secondary = client.email ?? null;

  return (
    <ListItemButton
      className={clsx(s.root, className)}
      selected={false}
      {...other}
    >
      <Box className={s.content}>
        <ListItemAvatar className={s.itemAvatar}>
          <CardAvatar className={s.avatar} src={client.photoUrl || undefined}>
            {name.charAt(0)}
          </CardAvatar>
        </ListItemAvatar>

        <ListItemText
          classes={{
            primary: s.primaryText,
            secondary: s.secondaryText,
          }}
          primary={name}
          secondary={secondary}
        />
      </Box>

      {singleSelect && (
        <Box className={s.selector}>
          <RadioIndicator selected={selected} />
        </Box>
      )}
    </ListItemButton>
  );
}
